/**
 * Utility functions for markdown processing and metadata extraction
 */
import React from 'react';
// import { detectLanguage } from './languageUtils';
import DOMPurify from 'dompurify';

/**
 * Validates and sanitizes metadata keys to prevent injection attacks
 * @param {string} key - The metadata key to validate
 * @returns {string|null} - Sanitized key or null if invalid
 */
const validateMetadataKey = (key) => {
  if (!key || typeof key !== 'string') return null;
  
  // Only allow alphanumeric characters, Hebrew characters, and common punctuation
  const validKeyRegex = /^[a-zA-Z0-9\u0590-\u05FF\s\-_]+$/;
  if (!validKeyRegex.test(key)) return null;
  
  // Limit key length
  if (key.length > 50) return null;
  
  return key.trim();
};

/**
 * Validates and sanitizes metadata values
 * @param {string} value - The metadata value to validate
 * @returns {string|null} - Sanitized value or null if invalid
 */
const validateMetadataValue = (value) => {
  if (!value || typeof value !== 'string') return null;
  
  // Limit value length
  if (value.length > 500) return null;
  
  // Remove potentially dangerous characters
  return value.replace(/[<>]/g, '').trim();
};

/**
 * Extracts metadata from markdown frontmatter with enhanced security
 * @param {string} markdown - Raw markdown content
 * @returns {object} - Object containing metadata and cleaned content
 */
export const extractMetadata = (markdown) => {
  if (!markdown || typeof markdown !== 'string') {
    return { metadata: {}, content: '' };
  }
  
  // Limit input size to prevent DoS (allow long guides e.g. glossary ~500KB)
  const MAX_MARKDOWN_LENGTH = 1000000;
  if (markdown.length > MAX_MARKDOWN_LENGTH) {
    console.warn('Markdown content too large, truncating');
    markdown = markdown.substring(0, MAX_MARKDOWN_LENGTH);
  }
  
  const metadata = {};
  let content = markdown;

  // Extract metadata section with improved regex
  const frontmatterRegex = /^---\s*\n([\s\S]*?)\n---\s*\n/;
  const match = frontmatterRegex.exec(markdown);
  
  if (match) {
    const frontmatter = match[1];
    content = markdown.replace(frontmatterRegex, '');
    
    // Parse the YAML frontmatter into an object with validation
    frontmatter.split('\n').forEach(line => {
      const colonIndex = line.indexOf(':');
      if (colonIndex !== -1) {
        const key = validateMetadataKey(line.slice(0, colonIndex).trim());
        const value = validateMetadataValue(line.slice(colonIndex + 1).trim().replace(/^"(.*)"$/, '$1'));
        
        if (key && value) {
          metadata[key] = value;
        }
      }
    });
  }

  return {
    metadata,
    content
  };
};

/**
 * Enhanced HTML sanitization to prevent XSS attacks
 * @param {string} html - HTML content to sanitize
 * @returns {string} - Sanitized HTML content
 */
export const sanitizeHtml = (html) => {
  if (!html || typeof html !== 'string') return '';
  
  // Additional security: remove script tags and event handlers
  const preSanitize = html
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    .replace(/\bon\w+\s*=/gi, '')
    .replace(/javascript:/gi, '');
  
  return DOMPurify.sanitize(preSanitize, {
    USE_PROFILES: { html: true },
    ALLOWED_TAGS: [
      'p', 'br', 'b', 'i', 'em', 'strong', 'a', 'ul', 'ol', 'li', 
      'code', 'pre', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 
      'blockquote', 'img', 'table', 'thead', 'tbody', 'tr', 'th', 'td', 
      'hr', 'sup', 'sub', 'span', 'div'
    ],
    ALLOWED_ATTR: [
      'href', 'name', 'target', 'src', 'alt', 'class', 'id', 
      'style', 'rel', 'title', 'width', 'height', 'lang', 'dir'
    ],
    ALLOW_DATA_ATTR: false,
    FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'form', 'input'],
    FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur'],
    KEEP_CONTENT: true,
    RETURN_DOM: false,
    RETURN_DOM_FRAGMENT: false,
    RETURN_TRUSTED_TYPE: false
  });
};

/**
 * Validates and sanitizes article metadata before display
 * @param {object} metadata - Raw metadata object
 * @returns {object} - Sanitized metadata object
 */
const sanitizeMetadata = (metadata) => {
  if (!metadata || typeof metadata !== 'object') return {};
  
  const sanitized = {};
  const allowedKeys = ['כותרת', 'title', 'תאריך', 'date', 'מחבר', 'author', 'קטגוריות', 'categories'];
  
  Object.keys(metadata).forEach(key => {
    if (allowedKeys.includes(key) && typeof metadata[key] === 'string') {
      const sanitizedValue = validateMetadataValue(metadata[key]);
      if (sanitizedValue) {
        sanitized[key] = sanitizedValue;
      }
    }
  });
  
  return sanitized;
};

/**
 * Component to display article metadata with enhanced security
 * @param {object} props - Component props
 * @param {object} props.metadata - Metadata object
 * @returns {JSX.Element|null} - Rendered metadata UI
 */
export const ArticleMetadata = ({ metadata }) => {
  if (!metadata || Object.keys(metadata).length === 0) return null;
  
  // Sanitize metadata before display
  const sanitizedMetadata = sanitizeMetadata(metadata);
  
  return (
    <div className="article-metadata" dir="rtl">
      {sanitizedMetadata.כותרת && (
        <h1 className="article-title">{sanitizedMetadata.כותרת}</h1>
      )}
      <div className="article-meta-info">
        {sanitizedMetadata.תאריך && (
          <span className="article-date">📅 {sanitizedMetadata.תאריך}</span>
        )}
        {sanitizedMetadata.מחבר && (
          <span className="article-author">✍️ {sanitizedMetadata.מחבר}</span>
        )}
        {sanitizedMetadata.קטגוריות && (
          <div className="article-categories">
            🏷️ {sanitizedMetadata.קטגוריות.split(',').map(cat => cat.trim()).join(' | ')}
          </div>
        )}
      </div>
      <hr className="metadata-divider" />
    </div>
  );
};

/**
 * Helper function to format date for Hebrew display with validation
 * @param {string} dateString - String representation of date
 * @returns {string} - Formatted date string
 */
export const formatHebrewDate = (dateString) => {
  if (!dateString || typeof dateString !== 'string') {
    return '';
  }
  
  try {
    // Validate date string format
    const date = new Date(dateString);
    
    // Check if date is valid and not too far in the future
    if (isNaN(date.getTime()) || date > new Date('2030-12-31')) {
      return dateString; // Return original string if invalid
    }
    
    // Format date in Hebrew locale with RTL support
    return date.toLocaleDateString('he-IL', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  } catch (e) {
    console.error('Error formatting date:', e);
    return dateString; // Fallback to original date string
  }
};
